import { SectionError } from "../AsyncSection/SectionError.js";
import React from "react";
import { cn } from "../../lib/utils.js";
import { Skeleton } from "../../atoms/Skeleton/index.js";
import { Checkbox } from "../../atoms/Checkbox/index.js";
import type { DataTableColumn, DataTableError } from "./DataTable.js";
import { resolveCardColumns } from "./resolve-card-columns.js";

/* ---------------- DataTableCards ----------------
   The `responsive` card list (D5, §17.5), below `md`.

   Split out of DataTable.tsx rather than grown inside it: that file is past this
   package's 200-line soft cap (§6), and the card list is a distinct layout
   rather than an inseparable part of the table.

   Why tiers instead of one flat <dl>: rendering every column as an equal-weight
   dt/dd pair meant an eight-column row became eight identical lines with the
   row's own name buried in the middle, and a column whose header is `sr-only`
   printed a visually blank label. Cards get read, not scanned column-wise, so
   they need a shape: identity, then state, then the one number that matters,
   then the rest.

   Accessibility note: only `meta` values carry a <dt>. The others are
   self-describing by construction — the title is the row's identity, a badge
   states its own value ("Active"), actions carry their own trigger label — with
   one exception: a bare metric would announce "68%" with no idea of what. So the
   metric renders its column header beside it as visible text, which is both the
   label and the design.

   Typography contract: slot wrappers set size and colour, not weight — a weight
   here is inherited by whatever `cell` returns and silently restyles content we
   do not own. Emphasis inside a cell belongs to the caller. The one deliberate
   exception is `metric`, which is a single value by definition, so emphasising
   all of it is the point rather than a leak. */

/* The current-row marker (`isRowCurrent`): NavList's CURRENT_BAR vocabulary,
   solid teal against the row's soft teal fill. Both layouts draw it, so it
   lives here and DataTable.tsx imports it (never the other way round, which
   would be an import cycle). In the table a bare `<span>` cannot be a direct
   child of `<tr>` (only `<td>`/`<th>` can), so it renders inside the row's
   first cell, which carries `relative` only when it is shown; on a card it is
   the `<li>`'s first child. */
export const CURRENT_ROW_BAR =
  "pointer-events-none absolute inset-y-0 start-0 w-0.5 bg-pb-teal";

export interface DataTableCardsProps<Row> {
  readonly columns: ReadonlyArray<DataTableColumn<Row>>;
  readonly data: ReadonlyArray<Row>;
  readonly getRowId: (row: Row) => string;
  readonly loading: boolean;
  readonly loadingRows: number;
  readonly isEmpty: boolean;
  readonly emptyState: React.ReactNode;
  /** The load failed — same presentation the table frame uses. */
  readonly error?: DataTableError;
  readonly selectable: boolean;
  readonly selected: ReadonlySet<string>;
  readonly onToggleRow: (id: string) => void;
  readonly getRowLabel?: ((row: Row) => string) | undefined;
  /** Marks the current card: `aria-current`, the soft teal fill and the bar. */
  readonly isRowCurrent?: ((row: Row) => boolean) | undefined;
}

export function DataTableCards<Row>({
  columns,
  data,
  getRowId,
  loading,
  loadingRows,
  isEmpty,
  emptyState,
  error,
  selectable,
  selected,
  onToggleRow,
  getRowLabel,
  isRowCurrent,
}: DataTableCardsProps<Row>): React.ReactElement {
  const { slots, meta } = resolveCardColumns(columns);
  const { title, subtitle, badge, metric, actions } = slots;

  /* Before `loading`: a failed load must not keep showing skeleton cards, which
     is the same branch-order defect this component exists to stop elsewhere. */
  if (error) {
    return (
      <div className="md:hidden">
        <SectionError
          title={error.title}
          description={error.description}
          {...(error.onRetry ? { onRetry: error.onRetry } : {})}
          {...(error.retryLabel ? { retryLabel: error.retryLabel } : {})}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="md:hidden">
        <ul className="space-y-2">
          {Array.from({ length: loadingRows }).map((_, i) => (
            <li
              key={`skc-${i}`}
              className="rounded-lg border border-pb-border bg-pb-card p-3"
            >
              <Skeleton className="h-4 w-2/3" />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (isEmpty) return <div className="md:hidden">{emptyState}</div>;

  return (
    <div className="md:hidden">
      <ul className="space-y-2">
        {data.map((row) => {
          const id = getRowId(row);
          const isSel = selected.has(id);
          const isCurrent = isRowCurrent?.(row) ?? false;
          return (
            <li
              key={id}
              data-selected={selectable && isSel ? "" : undefined}
              aria-current={isCurrent ? "true" : undefined}
              className={cn(
                "rounded-lg border border-pb-border p-3",
                isSel || isCurrent ? "bg-pb-teal-soft" : "bg-pb-card",
                /* Only when current: `relative` anchors the bar and
                   `overflow-hidden` clips it to the rounded corner. */
                isCurrent && "relative overflow-hidden",
              )}
            >
              {isCurrent && <span aria-hidden className={CURRENT_ROW_BAR} />}
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  {title !== undefined && (
                    /*
                      No font-weight here on purpose. `cell` returns arbitrary
                      nodes, and a weight on this wrapper is inherited by any of
                      them that does not set its own — which inverted the very
                      hierarchy above: a title cell rendering an identity at
                      `font-medium` plus an unstyled qualifier gave the qualifier
                      600 against the identity's 500. The caller owns emphasis
                      inside its own cell; we own placement.
                    */
                    <div className="text-sm text-pb-text">
                      {title.cell(row)}
                    </div>
                  )}
                  {subtitle !== undefined && (
                    <div className="text-dense-1 mt-0.5 text-pb-text-muted">
                      {subtitle.cell(row)}
                    </div>
                  )}
                </div>
                {selectable && (
                  <Checkbox
                    aria-label={getRowLabel ? getRowLabel(row) : "Select row"}
                    checked={isSel}
                    onChange={() => onToggleRow(id)}
                  />
                )}
                {actions !== undefined && (
                  <div className="flex-none">{actions.cell(row)}</div>
                )}
              </div>

              {(badge !== undefined || metric !== undefined) && (
                <div className="mt-3 flex items-center justify-between gap-3">
                  <span className="min-w-0">{badge?.cell(row)}</span>
                  {metric !== undefined && (
                    <span className="flex items-baseline gap-1.5">
                      <span className="pb-tabular text-base font-semibold text-pb-text">
                        {metric.cell(row)}
                      </span>
                      <span className="text-dense-2 uppercase tracking-wider text-pb-text-muted">
                        {metric.header}
                      </span>
                    </span>
                  )}
                </div>
              )}

              {meta.length > 0 && (
                <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-pb-border pt-3">
                  {meta.map((col) => (
                    <div key={col.id} className="min-w-0">
                      <dt className="text-dense-2 uppercase tracking-wider text-pb-text-muted">
                        {col.header}
                      </dt>
                      <dd
                        className={cn(
                          "text-sm text-pb-text",
                          /*
                            Numerals stay tabular, but the value is not
                            right-aligned: under a stacked label that reads as a
                            stray indent rather than a column.
                          */
                          col.align === "end" && "pb-tabular",
                        )}
                      >
                        {col.cell(row)}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
